import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  MessageCircle, X, Bug, Lightbulb, Heart, HelpCircle, 
  Send, Loader2, Check, Star
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEEDBACK_TYPES = [
  { id: 'love', icon: Heart, label: 'Love it!', color: 'text-pink-500 bg-pink-50 border-pink-200 hover:bg-pink-100 dark:bg-pink-900/20 dark:border-pink-800' },
  { id: 'feature', icon: Lightbulb, label: 'Feature idea', color: 'text-yellow-600 bg-yellow-50 border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:border-yellow-800' },
  { id: 'bug', icon: Bug, label: 'Found a bug', color: 'text-red-500 bg-red-50 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800' },
  { id: 'other', icon: HelpCircle, label: 'Other', color: 'text-blue-500 bg-blue-50 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800' },
];

function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState('');
  const [message, setMessage] = useState('');
  const [rating, setRating] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!type || !message.trim()) {
      toast.error('Please select a type and write a message');
      return;
    }

    setSending(true);
    try {
      await axios.post(
        `${API}/feedback`,
        {
          type,
          message: message.trim(),
          rating: type === 'love' ? rating : null,
          page: window.location.pathname
        },
        
      );
      
      setSent(true);
      setTimeout(() => {
        setIsOpen(false);
        setSent(false);
        setType('');
        setMessage('');
        setRating(0);
      }, 2000);
    } catch (error) {
      console.error('Failed to send feedback:', error);
      toast.error('Failed to send feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-40 w-11 h-11 bg-purple-100/80 dark:bg-purple-900/40 hover:bg-purple-200/80 dark:hover:bg-purple-800/50 backdrop-blur-sm text-purple-700 dark:text-purple-300 rounded-full shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 border border-purple-300/50 dark:border-purple-700/50"
        data-testid="feedback-trigger"
        title="Send feedback"
      >
        <MessageCircle className="w-5 h-5 mx-auto" />
      </button>
    );
  }

  return (
    <div 
      className="fixed bottom-20 right-4 z-50 w-80 sm:w-96 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border-2 border-purple-200/50 dark:border-purple-800/50 overflow-hidden"
      style={{
        boxShadow: '0 20px 60px rgba(168, 85, 247, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header with glossy gradient */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 px-4 py-3 flex items-center justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none"></div>
        <span className="text-white font-bold text-lg relative z-10 drop-shadow-md">Share Feedback</span>
        <button 
          onClick={() => setIsOpen(false)}
          className="text-white/90 hover:text-white relative z-10 hover:scale-110 transition-transform p-1 rounded-full hover:bg-white/20"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 sm:p-5">
        {sent ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Check className="w-8 h-8 text-white drop-shadow-md" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Thank you!</h3>
            <p className="text-gray-600 dark:text-gray-400">Your feedback helps us improve.</p>
          </div>
        ) : (
          <>
            {/* Type Selection */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              {FEEDBACK_TYPES.map((ft) => {
                const Icon = ft.icon;
                return (
                  <button
                    key={ft.id}
                    onClick={() => setType(ft.id)}
                    className={`flex items-center justify-center space-x-2 px-3 py-2.5 rounded-xl border-2 transition-all duration-200 ${
                      type === ft.id
                        ? ft.color + ' shadow-md scale-105'
                        : 'bg-gray-50 dark:bg-[#2A2520]/50 border-gray-200 dark:border-[#2A2520] hover:bg-gray-100 dark:hover:bg-gray-600 hover:scale-105'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${type === ft.id ? '' : 'text-gray-500 dark:text-gray-400'}`} />
                    <span className={`text-sm font-medium ${type === ft.id ? '' : 'text-gray-700 dark:text-gray-300'}`}>{ft.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Rating for "love it" */}
            {type === 'love' && (
              <div className="mb-4 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-xl border border-pink-200 dark:border-pink-800">
                <p className="text-sm font-medium text-pink-700 dark:text-pink-300 mb-2">How much do you love it?</p>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="p-1 hover:scale-125 transition-transform"
                    >
                      <Star
                        className={`w-7 h-7 transition-all duration-200 ${
                          star <= rating
                            ? 'text-yellow-400 fill-yellow-400 drop-shadow-md'
                            : 'text-gray-300 dark:text-gray-600 hover:text-yellow-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message */}
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                type === 'bug'
                  ? "What happened? What did you expect?"
                  : type === 'feature'
                  ? "Describe your idea..."
                  : type === 'love'
                  ? "What do you love about it?"
                  : "Tell us anything..."
              }
              className="min-h-[120px] resize-none mb-4 border-2 dark:bg-[#2A2520]/50 dark:border-[#2A2520] focus:border-purple-400 dark:focus:border-purple-600 rounded-xl"
              data-testid="feedback-message"
            />

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={!type || !message.trim() || sending}
              className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 hover:from-purple-600 hover:via-pink-600 hover:to-rose-600 text-white font-bold py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-white/20"
              data-testid="feedback-submit"
              style={{
                boxShadow: '0 4px 20px rgba(168, 85, 247, 0.4)',
              }}
            >
              {sending ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Send className="w-5 h-5 mr-2" />
              )}
              {sending ? 'Sending...' : 'Send Feedback'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default FeedbackWidget;
